import { useMemo } from "react";
import { notificacionError } from "@/components/Alerts.js";
import { useAppForm, useStore } from "@/hooks/useForm.js";
import { Input } from "@/components/ui/input.js";
import { QueryError } from "@/components/Loading/Loading.js";
import { useOPEsCompatibles, zInputsCrearOF, type Conjunto, type iInputsCrearOF } from "@/hooks/Api/OrdenesFabricacion.js";

interface FormArticuloProps {
	readonly id: string,
	readonly data: Conjunto | null,
	readonly onSubmit: (idArticulo: string, value: iInputsCrearOF) => Promise<void>;
	readonly closeModal: () => void;
	readonly idArticulo: string | null;
	readonly tipoArticulo: string;
	readonly showOF: boolean;
	readonly showOPE: boolean;
}
export function FormArticulo(props: FormArticuloProps) {
	const { data, onSubmit, idArticulo, tipoArticulo, showOF, showOPE, closeModal } = props;

	const form = useAppForm({
		defaultValues: {
			cantidadOrden: data?.cantidadOptima ?? "",
			inputPrioridad: 3,
			inputLote: "0",
			selectorProveedor: data?.defaultProveedor,
			selectorOPE: null,
			textoOF: "",
		} as iInputsCrearOF,
		validators: {
			onSubmit: zInputsCrearOF,
		},
		onSubmit: async ({ value }) => {
			if (data == null) throw new Error("Información no cargada");
			if (idArticulo == null) throw new Error("idArticulo no puede ser null");

			if (tipoArticulo === "OF+OPE") {
				if (value.selectorProveedor == "" || value.selectorProveedor == null) {
					notificacionError("Debe asignar un proveedor a OF con OPE");
					return;
				}

				if (value.selectorOPE == null) {
					notificacionError("Debe asignar una id de OPE");
					return;
				}
			}

			await onSubmit(idArticulo, value).then(() => {
				form.reset();
				closeModal();
			});

		},
	});

	const textoFases = useMemo(() => {
		return data?.ruta.fases.map((linea) => {
			if (linea.xcentro_id == "1") {
				return `${linea.xfaseruta_id} - ${linea.xdescripcion}`;
			} else if (linea.xcentro_id == "EXT") {	// tiene una ope
				return `${linea.xfaseruta_id} - CENTRO EXTERNO - ${linea.xdescripcion}`;
			} else {
				console.error("Ruta en almacen desconocido!");
				return "";
			}
		}) ?? [];
	}, [data]);

	const currentProveedor = useStore(form.store, (state) => state.values.selectorProveedor);
	const { data: OPEs, error: errorOPEs } = useOPEsCompatibles(currentProveedor);

	const proveedoresMap = useMemo(() => {
		const map: Record<string, string> = {};
		data?.proveedores.forEach(proveedor => {
			map[proveedor.idProveedor] = proveedor.nombre;
		});
		return map;
	}, [data?.proveedores]);
	const opesMap = useMemo(() => {
		const map: Record<string, string> = {
			"new": "Nueva OPE"
		};
		OPEs?.forEach(ope => {
			map[ope.idOPE] = `${ope.idOPE} - ${new Date(ope.fechaCreacion).toLocaleDateString()}`;
		});
		return map;
	}, [OPEs]);

	const handleSubmit = (event: React.SubmitEvent<HTMLFormElement>) => {
		event.preventDefault();
		event.stopPropagation();
		void form.handleSubmit();
	};

	if (errorOPEs) return <QueryError error={errorOPEs} fallback="Error al cargar los datos de fabricación" />;

	return (
		<form onSubmit={handleSubmit} key={props.idArticulo ?? "no-articulo"} id={props.id}>
			<div className="cabeceraCrearOF">
				<form.AppField name="cantidadOrden">
					{field => (
						<field.NumberField
							label="Cantidad"
							placeholder=""
							min={1}
							autoComplete="fabrication-quantity"
						/>
					)}
				</form.AppField>

				<Input label="Tipo de Artículo" readOnly value={tipoArticulo} />

				<form.AppField name="inputPrioridad">
					{field => showOF && (
						<field.NumberField
							label="Prioridad"
							placeholder=""
							min={1} max={5}
							autoComplete="fabrication-priority"
						/>
					)}
				</form.AppField>

				<Input label="Lote Económico" readOnly value={data?.cantidadOptima ?? ""} />

				<form.AppField name="inputLote">
					{field => showOPE && (
						<div>
							<label className="labelsForm">Acabado: </label>
							<field.SelectField
								isClearable
								options={{
									"0": "Normal",
									"AZUL": "Azul",
									"VERDE": "Verde",
									"ROJO": "Rojo",
								}}
							/>
						</div>
					)}
				</form.AppField>
			</div>

			<div className="cabeceraCrearOF" style={{ marginTop: "10px" }}>
				<form.AppField name="textoOF">
					{field => showOF && (
						<div>
							<label className="labelsForm">Texto OF: </label>
							<field.TextAreaField style={{ resize: "none", textAlign: "left" }} rows={5} />
						</div>
					)}
				</form.AppField>

				{showOF == true && (
					<div>
						<label className="labelsForm" style={{ width: "100%" }} >
							{`Ruta: ${data?.ruta.id} - ${data?.ruta.descripcion}`}
						</label>
						<textarea className="inputsForm" style={{ resize: "none", textAlign: "left", }} readOnly rows={5} value={textoFases.join("\n")} />
					</div>
				)}
			</div>

			<div className="cabeceraCrearOF" style={{ marginTop: 35, display: (showOPE || showOF) ? undefined : "none" }}>
				<form.AppField name="selectorProveedor">
					{field => showOPE && (
						<div>
							<label className="labelsForm">Proveedor: </label>
							<field.SelectField
								isClearable
								options={proveedoresMap}
							/>
						</div>
					)}
				</form.AppField>

				<form.AppField name="selectorOPE">
					{field => showOPE && (
						<div>
							<label className="labelsForm">OPE: </label>
							<field.SelectField
								isClearable
								options={opesMap}
							/>
						</div>
					)}
				</form.AppField>
			</div>

		</form>
	);
}
